import React, { useRef, useState, useEffect } from 'react';
import * as THREE from 'three';
import CloseIcon from '@mui/icons-material/Close';
import { useDesignStore } from 'context/DesignContext';
import { Engraving, EngravingResponseDto } from 'models/Engraving';
import { upsertEngraving } from 'services/engravingService';
import { useApi } from 'context/ApiProvider';
import { useViewerStore, ViewerTabsMode } from 'context/ViewerContext';
import toast from 'react-hot-toast';
import NumberSpinnerNoKeyboard from './Spinners/NumberSpinnerNoKeyboard';
import { Box, IconButton } from '@mui/material';
import { MeasurementSystem } from './Viewer3d/consts';
import { convertMmToViewerUnit, convertUnitToDefault, LengthUnit } from 'Utils/MeasurementUtils';

const EngravingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const engravingFromStore = useDesignStore((state) => state.engraving);
  const setEngraving = useDesignStore((state) => state.setEngraving);
  const engravingModelRef = useRef<Engraving>(useDesignStore.getState().engraving);
  const [engravingModel, setEngravingModel] = useState<Engraving>(engravingFromStore);
  const insoleDesign = useDesignStore((state) => state.insoleDesign);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const engravingDto = useRef<EngravingResponseDto>(new EngravingResponseDto());
  const api = useApi();
  const { setGCode } = useDesignStore.getState();
  const { updateFromViewer, setUpdateFromViewer, setUpdateFromPanel } = useViewerStore();

  const measurementSystem: MeasurementSystem = useViewerStore.getState().measurementSystem;
  const defaultLengthUnits: Record<MeasurementSystem, LengthUnit> = {
    [MeasurementSystem.metric]: 'mm',
    [MeasurementSystem.imperial]: 'inch',
  };

  const getDefaultLengthUnit = () => {
    return defaultLengthUnits[measurementSystem];
  }

  const [depthStep, setDepthStep] = useState<number>(0.1);
  const [heightStep, setHeightStep] = useState<number>(1);

  useEffect(() => {
    Object.assign(engravingModelRef.current, engravingFromStore);
    updateViewerModel();
    // setEngravingModel(engravingFromStore);
  }, [engravingFromStore]);

  useEffect(() => {
    const applyVoiceEngraving = () => {
      const next = useDesignStore.getState().engraving;
      Object.assign(engravingModelRef.current, next);
      setEngravingModel(Engraving.cloneFrom(next));
      createTextString();
      updateViewerModel();
    };
    document.addEventListener("voice:engravingUpdated", applyVoiceEngraving);
    return () => document.removeEventListener("voice:engravingUpdated", applyVoiceEngraving);
  }, []);

  useEffect(() => {
    if (measurementSystem == MeasurementSystem.metric) {
      setDepthStep(0.1);
      setHeightStep(1);
    }
    else {
      setDepthStep(0.005);
      setHeightStep(0.05);
    }

    updateViewerModel();
  }, [useViewerStore.getState().measurementSystem]);

  useEffect(() => {
    if (!insoleDesign) return;
    if (useViewerStore.getState().mode === ViewerTabsMode.Engraving) {
      engravingModelRef.current = useDesignStore.getState().engraving;
      engravingModelRef.current.textString = insoleDesign.name;
      createTextString();
      setUpdateFromPanel(true);
      setEngraving(engravingModelRef.current);
      updateViewerModel();

      // setEngravingModel(engravingModelRef.current);
    }
  }, [useViewerStore(state => state.mode)]);

  useEffect(() => {
    if (updateFromViewer) {
      setGCode([]);
      const updatePosition = (type: 'text' | 'UID' | 'CE', position?: THREE.Vector3Like) => {
        if (!position) return;

        engravingModelRef.current[`${type}Position`] = new THREE.Vector3(
          position.x,
          position.y,
          position.z
        );
        engravingDto.current[`${type}X`] = position.x;
        engravingDto.current[`${type}Z`] = position.z;
      };

      // === TEXT ===
      updatePosition('text', engravingFromStore.textPosition);
      createTextString();

      // === UID ===
      updatePosition('UID', engravingFromStore.UIDPosition);
      createUIDString();

      // === CE ===
      updatePosition('CE', engravingFromStore.CEPosition);
      createCEString();

      setEngraving(engravingModelRef.current);
      updateViewerModel();
      // setEngravingModel(engravingModelRef.current);

      resetSaveTimeout();
      setUpdateFromViewer(false);
    }
  }, [updateFromViewer]);

  const updateProperty = (field: keyof Engraving, input: HTMLInputElement) => {
    setGCode([]);
    const currentValue = (engravingModelRef.current as any)[field];
    let value: any;

    if (typeof currentValue === 'number') {
      if (['Depth', 'Height'].some(w => field.includes(w))) {
        value = convertUnitToDefault(Number(input.value), getDefaultLengthUnit());
      } else
        value = Number(input.value);
    } else if (typeof currentValue === 'boolean') {
      value = input.checked;
    } else {
      value = input.value;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    (engravingModelRef.current as any)[field] = value;
    (engravingDto.current as any)[field] = value;

    // setEngravingModel({ ...engravingModelRef.current } as Engraving);
    setEngraving(engravingModelRef.current);
    updateViewerModel();

    timeoutRef.current = setTimeout(() => {
      if (field.toLowerCase().includes('text')) {
        if (field.includes('Vertical')) {
          rotateMesh(engravingModelRef.current.textMesh, input.checked ? 1 : -1);
        } else {
          createTextString();
        }
      }

      if (field.includes('UID')) {
        if (field.includes('Vertical')) {
          rotateMesh(engravingModelRef.current.UIDMesh, input.checked ? 1 : -1);
        } else {
          createUIDString();
        }
      }

      if (field.includes('CE')) {
        if (field.includes('Vertical')) {
          rotateMesh(engravingModelRef.current.CEMesh, input.checked ? 1 : -1);
        } else {
          createCEString();
        }
      }
      setUpdateFromPanel(true);
      setEngraving(engravingModelRef.current);
      updateViewerModel();
      resetSaveTimeout();
    }, 400);
  };

  const resetSaveTimeout = () => {
    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
    }

    const engravingRequestDto = engravingDto.current.toEngravingRequestDto();
    savingTimeoutRef.current = setTimeout(() => {
      upsertEngraving(api, engravingRequestDto).catch((error) => {
        toast.error('Failed to save the engraving, please try again.');
      });
    }, 5000);
  };

  const rotateMesh = (mesh: THREE.Mesh[] | null, rotation: number) => {
    if (mesh) {
      mesh.forEach(m => m.geometry.rotateY(-Math.PI / 2 * rotation));
    }
  };

  const createTextString = () => {
    engravingModelRef.current.updateText();
    setEngraving(engravingModelRef.current);
    updateViewerModel();
  };

  const createUIDString = () => {
    engravingModelRef.current.updateUID();
    setEngraving(engravingModelRef.current);
    updateViewerModel();
  };

  const createCEString = () => {
    engravingModelRef.current.updateCE();
    setEngraving(engravingModelRef.current);
    updateViewerModel();
  };

  const updateViewerModel = () => {
    const copy = { ...engravingModelRef.current };

    for (const [key, value] of Object.entries(copy)) {
      if (['Depth', 'Height'].some(w => key.includes(w))) {
        (copy as any)[key] = convertMmToViewerUnit(Number(value));
      }
    }

    setEngravingModel(copy as Engraving);
  }

  return (
    <div className="property-panel-content">
      <Box className='property-panel-title-box'>
        <h3 className='property-panel-title'>Edit Engraving</h3>
        <IconButton sx={{ mx: 1, mb: '-8px', display: { lg: 'none' } }} onClick={onClose}>
          <CloseIcon sx={{ color: 'var(--text-color)' }} />
        </IconButton>
      </Box>
      <details className='property-panel-detail' open>
        <summary><b>Text</b></summary>
        <div className="property-group">
          <div className="property-item property-item-stacked">
            <label>Engrave Text</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                name="Engrave Text"
                checked={engravingModel.engraveText ?? undefined}
                onChange={e => updateProperty('engraveText', e.target)}
              />
            </div>
          </div>
          <div className="property-item property-item-stacked">
            <label>Text String</label>
            <input
              type="text"
              value={engravingModel.textString}
              onChange={e => updateProperty('textString', e.target)}
              className='property-input'
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Text Depth</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.textDepth}
              min={convertMmToViewerUnit(0.5)}
              max={convertMmToViewerUnit(1.5)}
              step={depthStep}
              onChange={(newValue: number) => {
                updateProperty('textDepth', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Text Height</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.textHeight}
              min={convertMmToViewerUnit(1)}
              max={convertMmToViewerUnit(10)}
              step={heightStep}
              onChange={(newValue: number) => {
                updateProperty('textHeight', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Text Repeat</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.textRepeat} min={1} max={10}
              onChange={(newValue: number) => {
                updateProperty('textRepeat', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Text Vertical</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                checked={engravingModel.textVertical ?? undefined}
                onChange={e => updateProperty('textVertical', e.target)}
              />
            </div>
          </div>
        </div>
      </details>
      <details className='property-panel-detail' open>
        <summary><b>UID</b></summary>
        <div className="property-group">
          <div className="property-item property-item-stacked">
            <label>Engrave UID</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                name="Engrave UID"
                checked={engravingModel.engraveUID ?? undefined}
                onChange={e => updateProperty('engraveUID', e.target)}
              />
            </div>
          </div>
          <div className="property-item property-item-stacked">
            <label>UID Depth</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.UIDDepth}
              min={convertMmToViewerUnit(0.5)}
              max={convertMmToViewerUnit(1.5)}
              step={depthStep}
              onChange={(newValue: number) => {
                updateProperty('UIDDepth', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>UID Height</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.UIDHeight}
              min={convertMmToViewerUnit(1)}
              max={convertMmToViewerUnit(10)}
              step={heightStep}
              onChange={(newValue: number) => {
                updateProperty('UIDHeight', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>UID Repeat</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.UIDRepeat} min={1} max={10}
              onChange={(newValue: number) => {
                updateProperty('UIDRepeat', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>UID Vertical</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                checked={engravingModel.UIDVertical ?? undefined}
                onChange={e => updateProperty('UIDVertical', e.target)}
              />
            </div>
          </div>
        </div>
      </details>
      <details className='property-panel-detail' open hidden>
        <summary><b>Logo</b></summary>
        <div className="property-group">
          <div className="property-item property-item-stacked">
            <label>Engrave Logo</label>
            <input
              type="checkbox"
              name="Engrave Logo"
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Logo Resolution</label>
            <input
              type="number"
              // value={150}
              min={50}
              max={500}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Logo Depth</label>
            <input
              type="number"
              min={0.5}
              max={1.5}
              step={0.1}
            // value={0.7}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Logo Smooth</label>
            <input
              type="number"
              min={1}
              max={10}
            // value={5}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Logo Width</label>
            <input
              type="number"
              min={1}
              max={10}
            // value={1}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>Logo Invert</label>
            <input
              type="checkbox"
            />
          </div>
        </div>
      </details>
      <details className='property-panel-detail' open>
        <summary><b>CE Mark</b></summary>
        <div className="property-group">
          <div className="property-item property-item-stacked">
            <label>Engrave CE Mark</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                name="Engrave CE"
                checked={engravingModel.engraveCE ?? undefined}
                onChange={e => updateProperty('engraveCE', e.target)}
              />
            </div>
          </div>
          <div className="property-item property-item-stacked">
            <label>CE Depth</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.CEDepth}
              min={convertMmToViewerUnit(0.5)}
              max={convertMmToViewerUnit(1.5)}
              step={depthStep}
              onChange={(newValue: number) => {
                updateProperty('CEDepth', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>CE Height</label>
            <NumberSpinnerNoKeyboard
              value={engravingModel.CEHeight}
              min={convertMmToViewerUnit(1)}
              max={convertMmToViewerUnit(10)}
              step={heightStep}
              onChange={(newValue: number) => {
                updateProperty('CEHeight', { value: newValue } as any);
              }}
            />
          </div>
          <div className="property-item property-item-stacked">
            <label>CE Vertical</label>
            <div className='property-checkbox-wrapper'>
              <input
                type="checkbox"
                checked={engravingModel.CEVertical ?? undefined}
                onChange={e => updateProperty('CEVertical', e.target)}
              />
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};

export default EngravingPanel;